/**
 * Article Detail Page
 *
 * Mobile-first detail view for a single article.
 */

import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Pencil, Package, Tag, AlertTriangle, Info, Trash2, RotateCcw, Printer } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import api from '@/lib/api'
import { t } from '@/i18n'
import { formatCurrency } from '@/lib/utils'
import { useDeleteArticle, useRestoreArticle } from '@/hooks/useArticles'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const deleteArticle = useDeleteArticle()
  const restoreArticle = useRestoreArticle()

  const { data: article, isLoading, error, refetch } = useQuery({
    queryKey: ['articles', Number(id)],
    queryFn: async () => {
      const response = await api.get(`/articles/${id}`)
      return response.data
    },
    enabled: !!id && !isNaN(Number(id)),
  })

  const isDeleted = !!article?.deleted_at
  const hasInvoices = (article?.invoice_count ?? 0) > 0

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !article) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Package className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('articles.notFound')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {error ? t('articles.errorLoading') : t('articles.doesNotExist')}
        </p>
        <Button variant="outline" onClick={() => navigate('/articles')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('articles.backToArticles')}
        </Button>
      </div>
    )
  }

  const isLowStock = article.count_articles <= article.minimum_stock

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/articles')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl md:text-2xl font-bold truncate">{article.code}</h1>
            {isLowStock && (
              <Badge variant="destructive" className="shrink-0">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {t('articles.lowStock')}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {article.description}
          </p>
        </div>
        {!isDeleted && (
          <Button variant="outline" size="icon" asChild className="h-10 w-10 shrink-0">
            <Link
              to={`/articles/labels?ids=${id}`}
              aria-label={t('labels.printArticle')}
              title={t('labels.printArticle')}
            >
              <Printer className="h-5 w-5" />
            </Link>
          </Button>
        )}
      </div>

      {/* Deleted Banner */}
      {isDeleted && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-destructive" />
            <span className="text-sm font-medium text-destructive">{t('articles.articleDeleted')}</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              await restoreArticle.mutateAsync(Number(id))
            }}
            disabled={restoreArticle.isPending}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            {t('articles.restoreArticle')}
          </Button>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex gap-2">
        {!isDeleted && (
          <Button asChild className="flex-1 h-12 md:flex-none md:w-auto">
            <Link to={`/articles/${id}/edit`}>
              <Pencil className="h-4 w-4 mr-2" />
              {t('articles.editArticle')}
            </Link>
          </Button>
        )}
        {!isDeleted && (
          <Button
            variant="outline"
            className="h-12 text-destructive hover:text-destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('articles.deleteArticle')}
          </Button>
        )}
      </div>

      {/* Stock Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            {t('articles.inventory')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground mb-1">{t('articles.currentStock')}</p>
              <p className={`text-2xl font-bold ${isLowStock ? 'text-red-500' : ''}`}>
                {article.count_articles}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground mb-1">{t('articles.minimumStock')}</p>
              <p className="text-2xl font-bold">{article.minimum_stock}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground mb-1">{t('articles.maximumStock')}</p>
              <p className="text-2xl font-bold">{article.maximum_stock}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-4 w-4" />
            {t('articles.prices')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Cost */}
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground mb-1">{t('articles.purchaseCost')}</p>
            <p className="text-lg font-bold">{formatCurrency(article.purchase_cost)}</p>
          </div>

          {/* Sale Prices */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-3 rounded-lg bg-primary/10 text-center">
              <p className="text-xs text-muted-foreground mb-1">{t('articles.price1')}</p>
              <p className="text-lg font-bold text-primary">
                {formatCurrency(article.sale_price_1_iva)}
              </p>
              <p className="text-xs text-muted-foreground">
                {article.profit_1}% {t('articles.margin')}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-xs text-muted-foreground mb-1">{t('articles.price2')}</p>
              <p className="text-lg font-bold">
                {formatCurrency(article.sale_price_2_iva)}
              </p>
              <p className="text-xs text-muted-foreground">
                {article.profit_2}% {t('articles.margin')}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 text-center">
              <p className="text-xs text-muted-foreground mb-1">{t('articles.price3')}</p>
              <p className="text-lg font-bold">
                {formatCurrency(article.sale_price_3_iva)}
              </p>
              <p className="text-xs text-muted-foreground">
                {article.profit_3}% {t('articles.margin')}
              </p>
            </div>
          </div>

          {/* Tax indicator */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <span className="text-sm">{t('articles.iva')}</span>
            <Badge variant={article.applied_sale_tax ? 'default' : 'secondary'}>
              {article.applied_sale_tax ? t('articles.included') : t('articles.notApplicable')}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Additional Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4" />
            {t('articles.additionalInfo')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {article.cabys_code && (
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">{t('articles.cabysCode')}</span>
              <span className="text-sm font-medium font-mono">{article.cabys_code}</span>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {article.ubication_name && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">{t('articles.location')}</p>
                <p className="text-sm font-medium">{article.ubication_name}</p>
              </div>
            )}
            {article.provider_name && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">{t('articles.provider')}</p>
                <p className="text-sm font-medium">{article.provider_name}</p>
              </div>
            )}
            {article.family_name && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">{t('articles.family')}</p>
                <p className="text-sm font-medium">{article.family_name}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('articles.deleteArticle')}: {article.code}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{article.description}</p>
                <p>
                  {hasInvoices
                    ? t('articles.confirmDeleteHasInvoices')
                    : t('articles.confirmDeletePermanent')
                  }
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await deleteArticle.mutateAsync({ id: Number(id) })
                setShowDeleteDialog(false)
                refetch()
              }}
            >
              {t('articles.deactivate')}
            </AlertDialogAction>
            {!hasInvoices && (
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async () => {
                  await deleteArticle.mutateAsync({ id: Number(id), permanent: true })
                  setShowDeleteDialog(false)
                  navigate('/articles')
                }}
              >
                {t('articles.deletePermanently')}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
